import {TimeSpan} from "@sparkware/app-utils";
import {action, makeObservable, observable} from "mobx";
import {delay} from "../../../utils/delay";
import {ServiceBase} from "../ServiceBase";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {IMagic8BallService} from "./magic-8-ball.service.interface";
import {InGameChallengesController_GetActiveTicketsResponse} from "../api/api.service";
import {DialogButtonClickHandleResultEnum} from "../dialog/dialog.service.interface";
import {IRegularTooltipOptions} from "../tooltip/regular-tooltip/regular-tooltip-options.interface";
import {SUB_GAME_GROUP} from "../../../common/const/sub_game_group_consts";
import {IGC_DEFAULT_TOOLTIP_TIMEOUT} from "../../../common/const/in-game-challenge.consts";
import {NEW_TICKET_NOTIFICATION} from "./new-ticket-notification-strategy.enum";

export class Magic8BallService extends ServiceBase implements IMagic8BallService {
    constructor(props) {
        super(props);

        makeObservable(this);

        this._initializeGameRestrictedTracking();
    }

    get allActiveCampaignRulesCompleted(): boolean {
        return this.services.inGameChallenge.allActiveCampaignRulesCompleted;
    }

    @observable
    showIntent: boolean = false;

    @observable.ref
    _magic8ballWrapper: HTMLDivElement | null = null;

    get magic8ballWrapper() {
        return this._magic8ballWrapper;
    }

    @action
    setMagic8BallWrapper(magic8ballWrapper: HTMLDivElement | null) {
        this._magic8ballWrapper = magic8ballWrapper;
    }

    @observable.ref
    _isGameRestricted: boolean = false;

    get isGameRestricted(): boolean {
        return this._isGameRestricted;
    }

    @action
    setIsGameRestricted(gameInfo) {
        if (gameInfo.isRoulette || gameInfo.isBaccarat) {
            this._isGameRestricted = true;
            return;
        }

        if (gameInfo.subGroupName === SUB_GAME_GROUP.LIVE_ROULETTE || gameInfo.subGroupName === SUB_GAME_GROUP.LIVE_BACCART) {
            this._isGameRestricted = true;
            return;
        }

        this._isGameRestricted = false;
    }

    private async _initializeGameRestrictedTracking() {
        const gameRunner = await this.services.gameRunner;
        this.reaction(() => gameRunner.currentRunningGame?.gameInfo, (gameInfo) => {
            if (!gameInfo) {
                return;
            }

            this.setIsGameRestricted(gameInfo);
        });
    }

    get shouldRenderBall() {
        if (!this.services.configuration.servicesConfigs.InGameChallenges.isMagic8BallEnabled || this.isGameRestricted) {
            return false;
        }

        return this.services.inGameChallenge.hasActiveCampaign;
    }

    @observable.ref
    newTicketNotificationStrategy: NEW_TICKET_NOTIFICATION;

    @action
    setNewTicketNotificationStrategy(strategy: NEW_TICKET_NOTIFICATION) {
        this.newTicketNotificationStrategy = strategy;
    }

    async showCampaignEndDateTooltip() {
        if (!this.magic8ballWrapper) {
            throw new Error('magic8ball failed to show campaign end time tooltip because magic8ballWrapper is not set');
        }

        const formattedEndDate = this.services.regulation.current.timeFormatter.formatUtcDateWithShortMonthAndTime(this.services.inGameChallenge.campaignEndDate.toISOString());
        const options: IRegularTooltipOptions = {
            attachTo: this.magic8ballWrapper,
            renderContent: () => (<>
                {this.services.translation.translate('IGC_FIRST_CAMPAIGN_CHALLENGE_COMPLETE_TOOLTIP', formattedEndDate)}
            </>),
            onTooltipRendered: () => {
                this.setNewTicketNotificationStrategy(NEW_TICKET_NOTIFICATION.BALL_PUMP);
                this.services.inGameChallenge.updateCampaignExpirationTooltipData();
            }
        };

        this._showTooltip(options);
    }

    async showInitialTooltip() {
        const shouldShowTooltip = this.services.localStorage.getItem(LocalStorageKeys.igcInitialTooltipShown) === null;
        if (!shouldShowTooltip) {
            return;
        }

        if (!this.magic8ballWrapper) {
            this.services.logger.debug('magic8ball failed to show initial tooltip because magic8ballWrapper is not set');
            return;
        }

        const options: IRegularTooltipOptions = {
            attachTo: this.magic8ballWrapper,
            renderContent: () => (<>
                {this.services.translation.translate('MAGIC_8_BALL_INITIAL_TOOLTIP_ROW1')}<br />
                {this.services.translation.translate('MAGIC_8_BALL_INITIAL_TOOLTIP_ROW2')}
            </>),
            onTooltipRendered: () => {
                this.services.localStorage.setItem(LocalStorageKeys.igcInitialTooltipShown, true);
                this.services.inGameChallenge.sendAnalytics('Show', 'IntroToolTip');
            }
        };

        this._showTooltip(options);
    }

    private async _showTooltip(options: IRegularTooltipOptions, executeDelay?: TimeSpan) {
        if (!(await this.services.gameRunner).currentRunningGame) {
            return;
        }

        executeDelay ?? await delay(executeDelay);

        const gameRunner = await this.services.gameRunner;

        const tooltipOptions = {
            timeout: TimeSpan.fromMilliseconds(IGC_DEFAULT_TOOLTIP_TIMEOUT),
            closeOnScroll: true,
            zIndex: gameRunner.gameFrameZIndex + 1,
            styling: {
                maxWidth: "45rem",
                fontSize: "1.6rem",
            },
            ...options
        }

        if (gameRunner.currentRunningGame) {
            return gameRunner.currentRunningGame.executeAnimationCompleteAction({
                execute: async () => {
                    if (gameRunner.currentRunningGame) { // We check for currentRunningGame because it possible that meanwhile the game to be closed and there is no current running game.
                        try {
                            await gameRunner.currentRunningGame.gameCommunicationChannel.stopAutoplay();
                        } catch (err) {
                            this.services.logger.error(`Magic8Ball Failed to stop autoplay for game ${gameRunner.currentRunningGame.gameInfo.gameType}`);
                        } finally {
                            await gameRunner.currentRunningGame.gameCommunicationChannel.resumeGamePlay();
                        }
                    }
                    this.services.regularTooltips.showTooltip(tooltipOptions);
                }
            });
        } else {
            return this.services.regularTooltips.showTooltip(tooltipOptions);
        }
    }

    @action
    showMagicBall() {
        this.showIntent = true;
    }

    @action
    hideMagicBall() {
        this.showIntent = false;
    }

    onError(screenErrorMsg: string | string[] | React.ReactElement, e?: Error) {
        if (e) {
            this.services.logger.error("Magic8Ball Error: " + e.message)
        } else {
            this.services.logger.error("Magic8Ball Error: " + screenErrorMsg)
        }
        this.hideMagicBall();
        this.services.dialog.showSimpleTextModal({
            text: screenErrorMsg,
            title: "An error occurred",
            iconSvg: this.services.theme.currentTheme.icons.warningSignIcon,
            hideCloseButton: false,
            buttons: [
                {
                    text: this.services.translation.translate('OK'),
                    buttonPreset: 'primary',
                    onClick: async () => {
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                    }
                }
            ]
        });
    }

    async getTicketsAsync(): Promise<InGameChallengesController_GetActiveTicketsResponse["data"]["tickets"]> {
        return (await this.services.api.inGameChallengesController.getActiveTickets()).data.tickets;
    }
}